import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Box,
  Grid,
  Table,
  CircularProgress,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Typography,
  TablePagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Tooltip,
  useMediaQuery,
  useTheme,
  Snackbar,
  Alert,
} from '@mui/material';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import { Delete, Edit, Add, FilterAltOff, AddPhotoAlternate, Close, InsertDriveFile } from '@mui/icons-material';
import { usersAPI, categoriesAPI, citiesAPI, districtsAPI, customerAPI } from '../../services/api';
import { clearFilterButtonSx } from '../../utils/clearFilterButtonSx';
import { useLanguage } from '../../context/LanguageContext';
import { getAdminUser } from '../../utils/authSessionStorage';
import browserGlobal from '../../utils/browserGlobal';
import { formatRoleDisplay } from '../../utils/roleUtils';

const getCategoryId = (category) => category?.id ?? category?.categoryId ?? '';
const getCategoryTitleEn = (category) =>
  category?.title_EN || category?.title_En || category?.categoryName || category?.name || category?.title || '';

/** Document slot: new upload (File) or existing registration doc from API (url + id for DELETE) */
const docEntryFromFile = (file) => ({ kind: 'local', file });
const docEntryFromRemote = (doc) => {
  const documentId = doc?.documentId ?? doc?.id ?? doc?.DocumentId ?? doc?.documentID;
  const url =
    doc?.url ??
    doc?.documentUrl ??
    doc?.fileUrl ??
    doc?.imageUrl ??
    doc?.downloadUrl ??
    doc?.path ??
    doc?.Url ??
    '';
  const name = doc?.fileName ?? doc?.name ?? doc?.FileName ?? '';
  const mimeType = doc?.mimeType ?? doc?.MimeType ?? '';
  if (!url && (documentId ?? '') === '') return null;
  return {
    kind: 'remote',
    documentId: documentId ?? null,
    url: String(url || ''),
    name: String(name || ''),
    mimeType: String(mimeType || '')
  };
};

const pickDocsByTypes = (documents, documentTypes, max = 2) => {
  if (!Array.isArray(documents) || !documentTypes?.length) return [];
  const typeSet = new Set(documentTypes.map((t) => String(t).toUpperCase()));
  const filtered = documents.filter((d) =>
    typeSet.has(String(d.documentType ?? d.DocumentType ?? '').toUpperCase())
  );
  const sorted = [...filtered].sort((a, b) => Number(b.id ?? 0) - Number(a.id ?? 0));
  return sorted
    .slice(0, max)
    .map(docEntryFromRemote)
    .filter(Boolean);
};

const pickFirstDocByTypes = (documents, documentTypes) => {
  const list = pickDocsByTypes(documents, documentTypes, 1);
  return list[0] || null;
};
const mapRemoteDocList = (arr) => {
  if (!Array.isArray(arr)) return [];
  return arr.map(docEntryFromRemote).filter(Boolean);
};

const defaultFormData = {
  userId: '',
  firstName: '',
  lastName: '',
  email: '',
  phoneNumber: '',
  country: '',
  role: 'Admin',
  accountType: 'Company',
  isEmailVerified: false,
  isBlocked: false
};

const Users = () => {
  const { t } = useLanguage();
  const theme = useTheme();
  const isMobileView = useMediaQuery(theme.breakpoints.down('sm'));
  const adminUser = getAdminUser();
  const isSuperAdmin = /super\s*admin/i.test(adminUser.role || '');

  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [toastOpen, setToastOpen] = useState(false);
  const [toast, setToast] = useState({ text: '', type: 'info' });
  const [users, setUsers] = useState([]);

  const showToast = (text, type = 'info') => {
    setToast({ text, type });
    setToastOpen(true);
  };
  const [selectedUser, setSelectedUser] = useState(null);
  const [open, setOpen] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedNameSearch, setDebouncedNameSearch] = useState('');
  const debouncedNameSearchRef = useRef(debouncedNameSearch);
  useEffect(() => {
    debouncedNameSearchRef.current = debouncedNameSearch;
  }, [debouncedNameSearch]);

  useEffect(() => {
    const id = browserGlobal.setTimeout(() => {
      const next = (searchQuery || '').trim();
      const prev = debouncedNameSearchRef.current;
      if (prev !== next) {
        setPage(0);
      }
      setDebouncedNameSearch(next);
    }, 400);
    return () => browserGlobal.clearTimeout(id);
  }, [searchQuery]);

  /** '' or display role: Admin | User | Customer | Super Admin */
  const [roleFilter, setRoleFilter] = useState('');
  const [deletingUserId, setDeletingUserId] = useState(null);
  const [customerPreviewLoading, setCustomerPreviewLoading] = useState(false);
  const [serviceTypes, setServiceTypes] = useState([]);
  const [serviceTypesLoading, setServiceTypesLoading] = useState(false);
  const [servicesLoaded, setServicesLoaded] = useState(false);

  const [formData, setFormData] = useState(defaultFormData);

  // Company form state
  const [businessName, setBusinessName] = useState('');
  const [companyAddress, setCompanyAddress] = useState('');
  const [companyServiceType, setCompanyServiceType] = useState('');
  const [addressDoc, setAddressDoc] = useState([]);
  const [city, setCity] = useState('');
  const [district, setDistrict] = useState('');
  const [cities, setCities] = useState([]);
  const [citiesLoading, setCitiesLoading] = useState(false);
  const [citiesLoaded, setCitiesLoaded] = useState(false);
  const [companyDistricts, setCompanyDistricts] = useState([]);
  const [freelancerDistricts, setFreelancerDistricts] = useState([]);
  const [companyDistrictsLoading, setCompanyDistrictsLoading] = useState(false);
  const [freelancerDistrictsLoading, setFreelancerDistrictsLoading] = useState(false);
  const [zipcode, setZipcode] = useState('');
  const [taxIdDoc, setTaxIdDoc] = useState([]);
  const [companyRegDoc, setCompanyRegDoc] = useState([]);
  const [companyLegalFormDoc, setCompanyLegalFormDoc] = useState([]);
  const [ownerName, setOwnerName] = useState('');
  const [nationalIdDoc, setNationalIdDoc] = useState([]);
  const [licenseIssuedState, setLicenseIssuedState] = useState('');
  const [professionalLicenseDoc, setProfessionalLicenseDoc] = useState([]);
  const [companyDescription, setCompanyDescription] = useState('');
  const [webUrl, setWebUrl] = useState('');

  // Freelancer form state
  const [freelancerServiceType, setFreelancerServiceType] = useState('');
  const [photo, setPhoto] = useState(null);
  const [freelancerAddressDoc, setFreelancerAddressDoc] = useState([]);
  const [freelancerCity, setFreelancerCity] = useState('');
  const [freelancerDistrict, setFreelancerDistrict] = useState('');
  const [freelancerZipcode, setFreelancerZipcode] = useState('');
  const [freelancerTaxIdDoc, setFreelancerTaxIdDoc] = useState([]);
  const [freelancerNationalIdDoc, setFreelancerNationalIdDoc] = useState([]);
  const [yearOfExpDoc, setYearOfExpDoc] = useState([]);
  const [freelancerLicenseIssuedState, setFreelancerLicenseIssuedState] = useState('');
  const [freelancerProfessionalLicenseDoc, setFreelancerProfessionalLicenseDoc] = useState([]);
  const [serviceDescription, setServiceDescription] = useState('');
  const [freelancerWebUrl, setFreelancerWebUrl] = useState('');
  /** From registration preview (profile / registrationForm) */
  const [contactTypeId, setContactTypeId] = useState('');

  const fetchUsers = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await usersAPI.getUsers(page, rowsPerPage, {
        name: debouncedNameSearch,
      });
      if (response?.data?.success === true && Array.isArray(response.data.payload)) {
        const list = response.data.payload;
        setUsers(list);
        const apiTotal = response.data.totalRecord;
        setTotalCount(
          apiTotal != null && Number(apiTotal) >= 0 ? Number(apiTotal) : list.length,
        );
      } else {
        setUsers([]);
        setTotalCount(0);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      setUsers([]);
      setTotalCount(0);
    } finally {
      setIsLoading(false);
    }
  }, [page, rowsPerPage, debouncedNameSearch]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const resetCustomerForm = () => {
    setBusinessName('');
    setCompanyAddress('');
    setCompanyServiceType('');
    setAddressDoc([]);
    setCity('');
    setDistrict('');
    setCompanyDistricts([]);
    setZipcode('');
    setTaxIdDoc([]);
    setCompanyRegDoc([]);
    setCompanyLegalFormDoc([]);
    setOwnerName('');
    setNationalIdDoc([]);
    setLicenseIssuedState('');
    setProfessionalLicenseDoc([]);
    setCompanyDescription('');
    setWebUrl('');

    setFreelancerServiceType('');
    setPhoto(null);
    setFreelancerAddressDoc([]);
    setFreelancerCity('');
    setFreelancerDistrict('');
    setFreelancerDistricts([]);
    setFreelancerZipcode('');
    setFreelancerTaxIdDoc([]);
    setFreelancerNationalIdDoc([]);
    setYearOfExpDoc([]);
    setFreelancerLicenseIssuedState('');
    setFreelancerProfessionalLicenseDoc([]);
    setServiceDescription('');
    setFreelancerWebUrl('');
    setContactTypeId('');
  };

  const toList = (response) => {
    if (!response?.data) return [];
    const d = response.data;
    if (Array.isArray(d?.payload)) return d.payload;
    if (Array.isArray(d?.data)) return d.data;
    if (Array.isArray(d)) return d;
    return [];
  };

  const fetchServiceTypes = useCallback(async () => {
    if (servicesLoaded) return;
    setServiceTypesLoading(true);
    try {
      const response = await categoriesAPI.getCategories(null, 0, 500);
      setServiceTypes(toList(response));
      setServicesLoaded(true);
    } catch (error) {
      console.error('Error fetching service types:', error);
      setServiceTypes([]);
    } finally {
      setServiceTypesLoading(false);
    }
  }, [servicesLoaded]);

  const fetchCities = useCallback(async () => {
    if (citiesLoaded) return;
    setCitiesLoading(true);
    try {
      const response = await citiesAPI.getCities(0, 500);
      setCities(toList(response));
      setCitiesLoaded(true);
    } catch (error) {
      console.error('Error fetching cities:', error);
      setCities([]);
    } finally {
      setCitiesLoading(false);
    }
  }, [citiesLoaded]);

  const getPreviewRoot = (preview) => {
    if (!preview || typeof preview !== 'object') return {};
    const company = preview.company || preview.Company || {};
    const freelancer = preview.freelancer || preview.Freelancer || {};
    return { ...preview, ...company, ...freelancer };
  };

  /**
   * Merge nested preview: account, profile, verification.*, registrationForm.
   * Later keys win so registrationForm overrides professionalInfo (e.g. businessName).
   */
  const parseCustomerRegistrationPayload = (preview) => {
    if (!preview || typeof preview !== 'object') return { p: {}, vDocuments: [] };
    const account = preview.account || {};
    const profileRaw = preview.profile || {};
    const companyRaw = profileRaw.company || {};
    const freelancerRaw = profileRaw.freelancer || {};
    const profileFlat = {
      ...profileRaw,
      ...companyRaw,
      ...freelancerRaw,
      companyBusinessName: profileRaw.company_BusinessName ?? profileRaw.companyBusinessName ?? companyRaw.businessName,
      freelancerFirstName: profileRaw.freelancer_FirstName ?? profileRaw.freelancerFirstName ?? freelancerRaw.firstName,
      freelancerLastName: profileRaw.freelancer_LastName ?? profileRaw.freelancerLastName ?? freelancerRaw.lastName
    };
    const registrationForm = preview.registrationForm || {};
    const verification = preview.verification || {};
    const status = verification.status || {};
    const professionalInfo = verification.professionalInfo || {};
    const vDocuments = Array.isArray(verification.documents) ? verification.documents : [];

    const nested =
      Boolean(preview.account) ||
      Boolean(preview.profile) ||
      Boolean(preview.verification) ||
      Boolean(preview.registrationForm);

    const base = nested ? {} : getPreviewRoot(preview);
    const p = {
      ...base,
      ...account,
      ...status,
      ...professionalInfo,
      ...profileFlat,
      ...registrationForm
    };
    return { p, vDocuments };
  };

  const applyCustomerRegistrationPreview = async (preview, userRow) => {
    const { p: merged, vDocuments } = parseCustomerRegistrationPayload(preview);
    const p = merged;
    const str = (v) => (v == null ? '' : String(v));

    const regType = String(p.registrationType ?? p.RegistrationType ?? '').toUpperCase();
    const legacyAcct =
      String(p.accountType ?? p.AccountType ?? userRow?.accountType ?? 'Company').toLowerCase() === 'freelancer'
        ? 'Freelancer'
        : 'Company';
    const at =
      regType === 'FREELANCER'
        ? 'Freelancer'
        : regType === 'COMPANY'
          ? 'Company'
          : legacyAcct;

    const useVerificationDocs = vDocuments.length > 0;

    setContactTypeId(str(p.contactTypeId ?? p.ContactTypeId ?? ''));

    setFormData((prev) => ({
      ...prev,
      ...(at === 'Freelancer'
        ? {
          firstName: str(
            p.firstName ??
            p.FirstName ??
            p.freelancerFirstName ??
            p.freelancer_FirstName ??
            userRow?.firstName
          ),
          lastName: str(
            p.lastName ?? p.LastName ?? p.freelancerLastName ?? p.freelancer_LastName ?? userRow?.lastName
          )
        }
        : { firstName: '', lastName: '' }),
      email: str(p.email ?? p.Email ?? userRow?.email ?? prev.email),
      phoneNumber: str(p.phoneNumber ?? p.PhoneNumber ?? p.phone ?? userRow?.phoneNumber),
      country: str(p.country ?? p.Country ?? userRow?.country),
      accountType: at,
      isEmailVerified: Boolean(p.isEmailVerified ?? p.IsEmailVerified ?? userRow?.isEmailVerified),
      isBlocked: Boolean(p.isBlocked ?? p.IsBlocked ?? userRow?.isBlocked)
    }));

    if (at === 'Freelancer') {
      setFreelancerServiceType(
        str(p.categoryId ?? p.serviceTypeId ?? p.serviceType ?? p.ServiceTypeId ?? userRow?.serviceTypeId ?? userRow?.serviceType)
      );
      setFreelancerCity(str(p.cityId ?? p.city ?? p.CityId ?? userRow?.cityId ?? userRow?.city));
      setFreelancerDistrict(str(p.districtId ?? p.district ?? p.DistrictId ?? userRow?.districtId ?? userRow?.district));
      setFreelancerZipcode(str(p.zipcode ?? p.Zipcode ?? p.postalCode ?? userRow?.zipcode));
      setFreelancerLicenseIssuedState(str(p.licenseIssuedState ?? p.LicenseIssuedState));
      setServiceDescription(str(p.serviceDescription ?? p.ServiceDescription ?? p.description));
      setFreelancerWebUrl(str(p.webUrl ?? p.WebUrl ?? p.websiteUrl));
      setCompanyAddress(str(p.companyAddress ?? p.CompanyAddress ?? p.address ?? p.Address ?? userRow?.companyAddress ?? userRow?.address ?? ''));

      if (useVerificationDocs) {
        setPhoto(
          pickFirstDocByTypes(vDocuments, [
            'PHOTO',
            'FREELANCER_PROFILE_PHOTO',
            'PROFILE_PHOTO',
            'PROFILE',
            'FREELANCER_PHOTO'
          ])
        );
        setFreelancerAddressDoc(pickDocsByTypes(vDocuments, ['ADDRESS_PROOF'], 2));
        setFreelancerTaxIdDoc(pickDocsByTypes(vDocuments, ['TAX_ID_DOCUMENT', 'TAX_ID'], 2));
        setFreelancerNationalIdDoc(pickDocsByTypes(vDocuments, ['NATIONAL_ID_OR_RESIDENCE_PERMIT', 'NATIONAL_ID'], 2));
        setYearOfExpDoc(
          pickDocsByTypes(vDocuments, ['YEAR_OF_EXPERIENCE', 'YEAR_OF_EXPERIENCE_DOCUMENT', 'EXPERIENCE_DOCUMENT'], 2)
        );
        setFreelancerProfessionalLicenseDoc(pickDocsByTypes(vDocuments, ['PROFESSIONAL_LICENSE'], 2));
      } else {
        const photoList = mapRemoteDocList(p.profilePhotoDocuments || p.photoDocuments || p.ProfilePhotoDocuments || p.photos);
        setPhoto(photoList[0] || null);
        setFreelancerAddressDoc(mapRemoteDocList(p.freelancerAddressDocuments || p.addressDocuments || p.AddressDocuments));
        setFreelancerTaxIdDoc(mapRemoteDocList(p.freelancerTaxIdDocuments || p.taxIdDocuments || p.TaxIdDocuments));
        setFreelancerNationalIdDoc(mapRemoteDocList(p.freelancerNationalIdDocuments || p.nationalIdDocuments || p.NationalIdDocuments));
        setYearOfExpDoc(mapRemoteDocList(p.yearOfExperienceDocuments || p.yearOfExpDocuments || p.YearOfExpDocuments));
        setFreelancerProfessionalLicenseDoc(
          mapRemoteDocList(p.freelancerProfessionalLicenseDocuments || p.professionalLicenseDocuments || p.ProfessionalLicenseDocuments)
        );
      }

      const cid = str(p.cityId ?? p.city ?? p.CityId);
      if (cid) await fetchDistrictOptions(cid, 'freelancer');
    } else {
      setBusinessName(
        str(
          p.businessName ??
          p.BusinessName ??
          p.companyBusinessName ??
          p.company_BusinessName ??
          userRow?.businessName ??
          ''
        )
      );
      setCompanyAddress(
        str(
          p.companyAddress ??
          p.CompanyAddress ??
          p.address ??
          p.Address ??
          userRow?.companyAddress ??
          userRow?.address ??
          ''
        )
      );
      setCompanyServiceType(
        str(p.categoryId ?? p.serviceTypeId ?? p.serviceType ?? p.ServiceTypeId ?? userRow?.serviceTypeId ?? userRow?.serviceType)
      );
      setCity(str(p.cityId ?? p.city ?? p.CityId ?? userRow?.cityId ?? userRow?.city));
      setDistrict(str(p.districtId ?? p.district ?? p.DistrictId ?? userRow?.districtId ?? userRow?.district));
      setZipcode(str(p.zipcode ?? p.Zipcode ?? p.postalCode ?? userRow?.zipcode));
      setOwnerName(str(p.ownerName ?? p.OwnerName));
      setLicenseIssuedState(str(p.licenseIssuedState ?? p.LicenseIssuedState));
      setCompanyDescription(str(p.companyDescription ?? p.CompanyDescription ?? p.description));
      setWebUrl(str(p.webUrl ?? p.WebUrl ?? p.websiteUrl));

      if (useVerificationDocs) {
        setAddressDoc(pickDocsByTypes(vDocuments, ['ADDRESS_PROOF'], 2));
        setTaxIdDoc(pickDocsByTypes(vDocuments, ['TAX_ID_DOCUMENT', 'TAX_ID'], 2));
        setCompanyRegDoc(pickDocsByTypes(vDocuments, ['COMPANY_REGISTRATION'], 2));
        setCompanyLegalFormDoc(pickDocsByTypes(vDocuments, ['COMPANY_LEGAL_FORM'], 2));
        setNationalIdDoc(pickDocsByTypes(vDocuments, ['NATIONAL_ID_OR_RESIDENCE_PERMIT', 'NATIONAL_ID'], 2));
        setProfessionalLicenseDoc(pickDocsByTypes(vDocuments, ['PROFESSIONAL_LICENSE'], 2));
      } else {
        setAddressDoc(mapRemoteDocList(p.addressDocuments || p.AddressDocuments || p.addressDocs));
        setTaxIdDoc(mapRemoteDocList(p.taxIdDocuments || p.TaxIdDocuments));
        setCompanyRegDoc(mapRemoteDocList(p.companyRegistrationDocuments || p.companyRegDocuments || p.CompanyRegDocuments));
        setCompanyLegalFormDoc(mapRemoteDocList(p.companyLegalFormDocuments || p.CompanyLegalFormDocuments));
        setNationalIdDoc(mapRemoteDocList(p.nationalIdDocuments || p.NationalIdDocuments));
        setProfessionalLicenseDoc(mapRemoteDocList(p.professionalLicenseDocuments || p.ProfessionalLicenseDocuments));
      }

      const cid = str(p.cityId ?? p.city ?? p.CityId);
      if (cid) await fetchDistrictOptions(cid, 'company');
    }
  };

  const fetchDistrictOptions = async (cityId, type) => {
    if (!cityId) {
      if (type === 'company') {
        setCompanyDistricts([]);
      } else {
        setFreelancerDistricts([]);
      }
      return;
    }

    if (type === 'company') setCompanyDistrictsLoading(true);
    if (type === 'freelancer') setFreelancerDistrictsLoading(true);

    try {
      const response = await districtsAPI.getDistricts(cityId, 0, 500);
      const list = toList(response);
      if (type === 'company') setCompanyDistricts(list);
      if (type === 'freelancer') setFreelancerDistricts(list);
    } catch (error) {
      console.error('Error fetching districts:', error);
      if (type === 'company') setCompanyDistricts([]);
      if (type === 'freelancer') setFreelancerDistricts([]);
    } finally {
      if (type === 'company') setCompanyDistrictsLoading(false);
      if (type === 'freelancer') setFreelancerDistrictsLoading(false);
    }
  };

  const normalizeRoleForDisplay = (role) => {
    if (!role) return 'User';
    return formatRoleDisplay(role) || 'User';
  };

  const matchesRoleFilter = (user) => {
    if (!roleFilter) return true;
    return normalizeRoleForDisplay(user.role) === roleFilter;
  };

  /** Name-only (and API casing variants); does not match email, role, or timestamps. */
  const matchesSearchQuery = (user) => {
    if (debouncedNameSearch === '') return true;
    const query = debouncedNameSearch.toLowerCase();
    const firstName = String(user.firstName ?? user.FirstName ?? '').toLowerCase();
    const lastName = String(user.lastName ?? user.LastName ?? '').toLowerCase();
    const full = `${firstName} ${lastName}`.trim();
    return (
      firstName.includes(query) ||
      lastName.includes(query) ||
      full.includes(query)
    );
  };

  const visibleUsers = useMemo(
    () => users.filter((user) => matchesSearchQuery(user) && matchesRoleFilter(user)),
    [users, debouncedNameSearch, roleFilter],
  );

  const handleOpen = async (user = null) => {
    await Promise.all([fetchServiceTypes(), fetchCities()]);
    resetCustomerForm();

    if (user) {
      setSelectedUser(user);
      const roleLabel = normalizeRoleForDisplay(user.role);
      const acctFreelancer = String(user.accountType || 'Company').toLowerCase() === 'freelancer';
      const companyCustomer = roleLabel === 'Customer' && !acctFreelancer;
      setFormData({
        ...defaultFormData,
        userId: user.userId || user.id || '',
        firstName: companyCustomer ? '' : user.firstName || '',
        lastName: companyCustomer ? '' : user.lastName || '',
        email: user.email || '',
        phoneNumber: user.phoneNumber || '',
        country: user.country || '',
        role: roleLabel,
        accountType: acctFreelancer ? 'Freelancer' : 'Company',
        isEmailVerified: user.isEmailVerified || false,
        isBlocked: user.isBlocked || false
      });

      if (normalizeRoleForDisplay(user.role) === 'Customer') {
        const uid = user.userId || user.id;
        setCustomerPreviewLoading(true);
        try {
          const res = await customerAPI.getRegistrationPreview(uid);
          const preview = res?.data?.payload ?? res?.data?.data ?? res?.data;
          if (preview && typeof preview === 'object') {
            await applyCustomerRegistrationPreview(preview, user);
          } else {
            await applyCustomerRegistrationPreview({}, user);
          }
        } catch (err) {
          console.error('Customer registration preview:', err);
          await applyCustomerRegistrationPreview({}, user);
        } finally {
          setCustomerPreviewLoading(false);
        }
      }
    } else {
      setSelectedUser(null);
      setFormData(defaultFormData);
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedUser(null);
    resetCustomerForm();
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'role' && value === 'Customer') {
      setFormData((prev) => ({
        ...prev,
        role: 'Customer',
        accountType: 'Company',
        firstName: '',
        lastName: '',
        country: prev.country || 'Germany',
      }));
      return;
    }

    if (name === 'role' && value !== 'Customer') {
      setFormData((prev) => ({
        ...prev,
        role: value,
        accountType: 'Company'
      }));
      return;
    }

    if (name === 'accountType') {
      const nextAcct = value;
      const isFreel = String(nextAcct).toLowerCase() === 'freelancer';
      setFormData((prev) => ({
        ...prev,
        accountType: nextAcct,
        ...(isFreel ? {} : { firstName: '', lastName: '' }),
      }));
      return;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const resolveApiRole = (roleLabel) => {
    if (roleLabel === 'Admin') return 'admin';
    if (roleLabel === 'Admin User' || roleLabel === 'AdminUser') return 'adminuser';
    if (roleLabel === 'Admin Event' || roleLabel === 'AdminEvent') return 'AdminEvent';
    if (roleLabel === 'Admin Accommodation' || roleLabel === 'Admin Accomodation' || roleLabel === 'AdminAccommodation' || roleLabel === 'AdminAccomodation') return 'AdminAccomodation';
    if (roleLabel === 'Admin Contact' || roleLabel === 'AdminContact') return 'AdminContact';
    if (roleLabel === 'Admin Promotion' || roleLabel === 'AdminPromotion') return 'AdminPromotion';
    if (roleLabel === 'Customer') return 'customer';
    if (roleLabel === 'Super Admin') return 'superadmin';
    return 'user';
  };

  const buildUserPayload = () => {
    return {
      userId: selectedUser ? (selectedUser.userId || 0) : 0,
      firstName: formData.firstName || '',
      lastName: formData.lastName || '',
      email: formData.email || '',
      phoneNumber: formData.phoneNumber || '',
      country: formData.country || '',
      role: resolveApiRole(formData.role),
      isEmailVerified: Boolean(formData.isEmailVerified),
      isBlocked: Boolean(formData.isBlocked)
    };
  };

  const appendDocEntries = (fd, key, entries) => {
    (entries || []).forEach((e) => {
      if (e?.kind === 'local' && e.file) fd.append(key, e.file);
    });
  };

  const buildCustomerPayload = () => {
    const fd = new FormData();
    const isFreelancer = String(formData.accountType || '').toLowerCase() === 'freelancer';

    if (selectedUser) {
      const uid = selectedUser.userId || selectedUser.id;
      if (uid) fd.append('userId', uid);
    }
    fd.append('role', 'customer');
    fd.append('accountType', formData.accountType);
    fd.append('email', formData.email || '');
    fd.append('phoneNumber', formData.phoneNumber || '');
    fd.append('country', formData.country || '');
    fd.append('isEmailVerified', String(Boolean(formData.isEmailVerified)));
    fd.append('isBlocked', String(Boolean(formData.isBlocked)));
    if (contactTypeId !== '' && contactTypeId != null) {
      fd.append('contactTypeId', String(contactTypeId));
    }

    if (isFreelancer) {
      fd.append('firstName', formData.firstName || '');
      fd.append('lastName', formData.lastName || '');
      fd.append('companyAddress', companyAddress || '');
      fd.append('serviceType', freelancerServiceType || '');
      fd.append('cityId', freelancerCity != null && freelancerCity !== '' ? String(freelancerCity) : '');
      fd.append('districtId', freelancerDistrict != null && freelancerDistrict !== '' ? String(freelancerDistrict) : '');
      fd.append('city', freelancerCity || '');
      fd.append('district', freelancerDistrict || '');
      fd.append('zipcode', freelancerZipcode || '');
      fd.append('licenseIssuedState', freelancerLicenseIssuedState || '');
      fd.append('serviceDescription', serviceDescription || '');
      fd.append('webUrl', freelancerWebUrl || '');
      if (photo?.kind === 'local' && photo.file) fd.append('photo', photo.file);

      appendDocEntries(fd, 'freelancerAddressDoc', freelancerAddressDoc);
      appendDocEntries(fd, 'freelancerTaxIdDoc', freelancerTaxIdDoc);
      appendDocEntries(fd, 'freelancerNationalIdDoc', freelancerNationalIdDoc);
      appendDocEntries(fd, 'yearOfExpDoc', yearOfExpDoc);
      appendDocEntries(fd, 'freelancerProfessionalLicenseDoc', freelancerProfessionalLicenseDoc);
    } else {
      fd.append('businessName', businessName || '');
      fd.append('companyAddress', companyAddress || '');
      fd.append('serviceType', companyServiceType || '');
      fd.append('cityId', city != null && city !== '' ? String(city) : '');
      fd.append('districtId', district != null && district !== '' ? String(district) : '');
      fd.append('city', city || '');
      fd.append('district', district || '');
      fd.append('zipcode', zipcode || '');
      fd.append('ownerName', ownerName || '');
      fd.append('licenseIssuedState', licenseIssuedState || '');
      fd.append('companyDescription', companyDescription || '');
      fd.append('webUrl', webUrl || '');

      appendDocEntries(fd, 'addressDoc', addressDoc);
      appendDocEntries(fd, 'taxIdDoc', taxIdDoc);
      appendDocEntries(fd, 'companyRegDoc', companyRegDoc);
      appendDocEntries(fd, 'companyLegalFormDoc', companyLegalFormDoc);
      appendDocEntries(fd, 'nationalIdDoc', nationalIdDoc);
      appendDocEntries(fd, 'professionalLicenseDoc', professionalLicenseDoc);
    }

    return fd;
  };

  const validateForm = () => {
    if (!formData.email) {
      showToast(t('users.errEmailRequired'), 'error');
      return false;
    }

    if (formData.role !== 'Customer') {
      if (!formData.firstName || !formData.lastName) {
        showToast(t('users.errFirstLastRequired'), 'error');
        return false;
      }
      return true;
    }

    if (!formData.phoneNumber) {
      showToast(t('users.errPhoneRequired'), 'error');
      return false;
    }

    if (!formData.country) {
      showToast(t('users.errCountryRequired'), 'error');
      return false;
    }

    if (String(formData.accountType || '').toLowerCase() !== 'freelancer') {
      if (!businessName) {
        showToast(t('users.errFullNameRequired'), 'error');
        return false;
      }
      if (!companyServiceType) {
        showToast(t('users.errServiceTypeRequired'), 'error');
        return false;
      }
      if (!city) {
        showToast(t('users.errCityRequired'), 'error');
        return false;
      }
      if (!district) {
        showToast(t('users.errDistrictRequired'), 'error');
        return false;
      }
      if (!zipcode) {
        showToast(t('users.errZipcodeRequired'), 'error');
        return false;
      }
      if (!ownerName) {
        showToast(t('users.errNameRequired'), 'error');
        return false;
      }
      if (!companyRegDoc.length || !nationalIdDoc.length) {
        showToast(t('users.errCompanyDocsRequired'), 'error');
        return false;
      }
    } else {
      if (!formData.firstName) {
        showToast(t('users.fieldFirstName') + ' is required.', 'error');
        return false;
      }
      if (!formData.lastName) {
        showToast(t('users.fieldLastName') + ' is required.', 'error');
        return false;
      }
      if (!freelancerServiceType) {
        showToast(t('users.errServiceTypeRequired'), 'error');
        return false;
      }
      if (!freelancerCity) {
        showToast(t('users.errCityRequired'), 'error');
        return false;
      }
      if (!freelancerDistrict) {
        showToast(t('users.errDistrictRequired'), 'error');
        return false;
      }
      if (!freelancerZipcode) {
        showToast(t('users.errZipcodeRequired'), 'error');
        return false;
      }
      if (!freelancerNationalIdDoc.length) {
        showToast(t('users.errFreelancerDocsRequired'), 'error');
        return false;
      }
    }

    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    setIsSubmitting(true);
    try {
      if (formData.role === 'Customer') {
        const fd = buildCustomerPayload();
        if (selectedUser) {
          const uid = selectedUser.userId || selectedUser.id;
          await customerAPI.updateRegistrationPreview(uid, fd);
        } else {
          await customerAPI.registerFullyVerified(fd);
        }
      } else {
        const payload = buildUserPayload();
        if (selectedUser) {
          await usersAPI.updateUser(payload);
        } else {
          await usersAPI.createUser(payload);
        }
      }
      fetchUsers();
      handleClose();
      showToast(selectedUser ? t('users.toastUserUpdated') : t('users.toastUserAdded'), 'success');
    } catch (error) {
      console.error('Error saving user:', error);
      const data = error?.response?.data;
      let errMsg =
        data?.errorMessage ||
        data?.message ||
        data?.detail ||
        (data?.errors ? Object.values(data.errors).flat().join(', ') : null);
      if (!errMsg && error?.response?.status === 409) {
        errMsg = t('users.errConflict409');
      }
      showToast(errMsg || error?.message || t('users.errSaveFailed'), 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (user) => {
    const userId = user?.userId ?? user?.id;
    if (!userId) return;
    if (!window.confirm(t('users.confirmDelete'))) return;
    setDeletingUserId(userId);
    try {
      await usersAPI.deleteUser(userId);
      fetchUsers();
      showToast('User deleted successfully.', 'success');
    } catch (error) {
      console.error('Error deleting user:', error);
      showToast(error?.response?.data?.errorMessage || error?.message || t('users.deleteFailed'), 'error');
    } finally {
      setDeletingUserId(null);
    }
  };

  const dashedAddTileSx = {
    width: 80,
    height: 80,
    border: '2px dashed #d1d5db',
    borderRadius: '12px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#6b7280',
    textTransform: 'none',
    transition: 'all 0.2s',
    '&:hover': {
      borderColor: '#0F3D3E',
      color: '#0F3D3E',
      bgcolor: 'rgba(124, 58, 237, 0.04)',
      transform: 'translateY(-2px)',
    },
  };

  const docDropzoneContainerSx = {
    p: 2,
    border: '1px solid #e5e7eb',
    borderRadius: '12px',
    bgcolor: '#f9fafb',
    minHeight: '100px',
  };

  const handleAppendDocEntries = (setter, existingEntries) => (event) => {
    const picked = Array.from(event.target.files || []);
    event.target.value = '';
    if (!picked.length) return;
    const newLocals = picked.map((f) => docEntryFromFile(f));
    const merged = [...existingEntries, ...newLocals].slice(0, 2);
    setter(merged);
    if (existingEntries.length + picked.length > 2) {
      alert('Maximum 2 documents can be uploaded.');
    }
  };

  const removeDocEntryAt = async (setter, entries, index) => {
    const list = [...entries];
    const entry = list[index];
    if (!entry) return;
    if (entry.kind === 'remote' && entry.documentId != null && entry.documentId !== '') {
      const uid = selectedUser?.userId ?? selectedUser?.id ?? formData.userId;
      if (uid) {
        try {
          await customerAPI.deleteRegistrationDocument(uid, entry.documentId);
        } catch (err) {
          alert(err?.response?.data?.errorMessage || err?.message || 'Failed to delete document');
          return;
        }
      }
    }
    setter(list.filter((_, i) => i !== index));
  };

  const removePhotoEntry = async () => {
    if (photo?.kind === 'remote' && photo.documentId != null && photo.documentId !== '') {
      const uid = selectedUser?.userId ?? selectedUser?.id ?? formData.userId;
      if (uid) {
        try {
          await customerAPI.deleteRegistrationDocument(uid, photo.documentId);
        } catch (err) {
          alert(err?.response?.data?.errorMessage || err?.message || 'Failed to delete photo');
          return;
        }
      }
    }
    setPhoto(null);
  };

  const isImageFile = (file) => file && typeof file.type === 'string' && file.type.startsWith('image/');

  const isDocEntryImage = (entry) => {
    if (!entry) return false;
    if (entry.kind === 'local') return isImageFile(entry.file);
    const m = String(entry.mimeType || '').toLowerCase();
    if (m.startsWith('image/')) return true;
    const u = String(entry.url || '').toLowerCase();
    return /\.(jpe?g|png|gif|webp|bmp|svg)(\?|#|$)/i.test(u);
  };

  const renderDocumentCategorySlot = (title, entries, setter, required = false) => (
    <Box>
      <Tooltip title={title} placement="top">
        <Typography
          variant="subtitle2"
          sx={{
            fontWeight: 600,
            color: '#374151',
            mb: 1,
            display: 'block',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {title}
          {required && <span style={{ color: '#d32f2f', marginLeft: '4px' }}>*</span>}
          {!required && <span style={{ color: '#6b7280', marginLeft: '4px', fontWeight: 'normal', fontSize: '0.85em' }}>(Optional)</span>}
        </Typography>
      </Tooltip>
      <Box sx={{ minHeight: 88 }}>
        <Grid container spacing={2}>
          {entries.map((entry, idx) => (
            <Grid item key={`${title}-${idx}-${entry.kind}-${entry.kind === 'local' ? entry.file?.name : entry.url}`}>
              <Box
                sx={{
                  position: 'relative',
                  width: 80,
                  height: 80,
                  '&:hover .doc-delete-btn': { opacity: 1 },
                }}
              >
                {isDocEntryImage(entry) ? (
                  <img
                    src={entry.kind === 'local' ? URL.createObjectURL(entry.file) : entry.url}
                    alt=""
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      borderRadius: '12px',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                    }}
                  />
                ) : (
                  <Box
                    sx={{
                      width: '100%',
                      height: '100%',
                      borderRadius: '12px',
                      bgcolor: '#e5e7eb',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.06)',
                    }}
                  >
                    <InsertDriveFile sx={{ fontSize: 36, color: '#6b7280' }} />
                  </Box>
                )}
                <IconButton
                  size="small"
                  className="doc-delete-btn"
                  onClick={() => removeDocEntryAt(setter, entries, idx)}
                  sx={{
                    position: 'absolute',
                    top: -8,
                    right: -8,
                    bgcolor: '#fff',
                    opacity: 0.85,
                    transition: 'all 0.2s',
                    '&:hover': { bgcolor: '#ef4444', color: '#fff', opacity: 1 },
                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                    p: 0.5,
                  }}
                >
                  <Close sx={{ fontSize: 16 }} />
                </IconButton>
              </Box>
            </Grid>
          ))}
          {entries.length < 2 && (
            <Grid item>
              <Button component="label" sx={dashedAddTileSx}>
                <AddPhotoAlternate sx={{ fontSize: 28 }} />
                <Typography variant="caption" sx={{ mt: 0.5, fontWeight: 500 }}>
                  {t('common.add') || 'Add'}
                </Typography>
                <input
                  type="file"
                  hidden
                  multiple
                  accept="image/*,.pdf,.doc,.docx"
                  onChange={handleAppendDocEntries(setter, entries)}
                />
              </Button>
            </Grid>
          )}
        </Grid>
      </Box>
    </Box>
  );

  const renderPhotoSlot = (title, photoValue, required = false) => (
    <Box>
      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#374151', mb: 1, display: 'block' }}>
        {title}
        {required && <span style={{ color: '#d32f2f', marginLeft: '4px' }}>*</span>}
        {!required && <span style={{ color: '#6b7280', marginLeft: '4px', fontWeight: 'normal', fontSize: '0.85em' }}>(Optional)</span>}
      </Typography>
      <Box sx={{ minHeight: 88 }}>
        <Grid container spacing={2}>
          {photoValue && (
            <Grid item>
              <Box sx={{ position: 'relative', width: 80, height: 80, '&:hover .doc-delete-btn': { opacity: 1 } }}>
                {photoValue.kind === 'local' ? (
                  isImageFile(photoValue.file) ? (
                    <img
                      src={URL.createObjectURL(photoValue.file)}
                      alt=""
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        borderRadius: '12px',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                      }}
                    />
                  ) : (
                    <Box
                      sx={{
                        width: '100%',
                        height: '100%',
                        borderRadius: '12px',
                        bgcolor: '#e5e7eb',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <InsertDriveFile sx={{ fontSize: 36, color: '#6b7280' }} />
                    </Box>
                  )
                ) : isDocEntryImage(photoValue) ? (
                  <img
                    src={photoValue.url}
                    alt=""
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      borderRadius: '12px',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                    }}
                  />
                ) : (
                  <Box
                    sx={{
                      width: '100%',
                      height: '100%',
                      borderRadius: '12px',
                      bgcolor: '#e5e7eb',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <InsertDriveFile sx={{ fontSize: 36, color: '#6b7280' }} />
                  </Box>
                )}
                <IconButton
                  size="small"
                  className="doc-delete-btn"
                  onClick={() => removePhotoEntry()}
                  sx={{
                    position: 'absolute',
                    top: -8,
                    right: -8,
                    bgcolor: '#fff',
                    opacity: 0.85,
                    '&:hover': { bgcolor: '#ef4444', color: '#fff', opacity: 1 },
                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                    p: 0.5,
                  }}
                >
                  <Close sx={{ fontSize: 16 }} />
                </IconButton>
              </Box>
            </Grid>
          )}
          {!photoValue && (
            <Grid item>
              <Button
                component="label"
                sx={dashedAddTileSx}
              >
                <AddPhotoAlternate sx={{ fontSize: 28 }} />
                <Typography variant="caption" sx={{ mt: 0.5, fontWeight: 500 }}>
                  {t('common.add') || 'Add'}
                </Typography>
                <input
                  type="file"
                  hidden
                  accept="image/*,.pdf,.doc,.docx"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) setPhoto(docEntryFromFile(f));
                    e.target.value = '';
                  }}
                />
              </Button>
            </Grid>
          )}
        </Grid>
      </Box>
    </Box>
  );

  const renderDocumentsGrid2x3 = (sectionTitle, slots) => (
    <Grid item xs={12}>
      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#374151', mt: 1, mb: 1 }}>
        {sectionTitle}
      </Typography>
      <Box sx={{ ...docDropzoneContainerSx, mb: 0 }}>
        <Grid container spacing={2}>
          {slots.map((slot) => (
            <Grid item xs={12} sm={6} key={slot.key}>
              {slot.render()}
            </Grid>
          ))}
        </Grid>
      </Box>
    </Grid>
  );

  const isCustomerRole = formData.role === 'Customer';
  const isFreelancerAccount = String(formData.accountType || '').toLowerCase() === 'freelancer';

  return (
    <Box p={1}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        mb={2}
        flexDirection={{ xs: 'column', sm: 'row' }}
        flexWrap="wrap"
        gap={1}
      >
        <Typography variant="h4" color="primary">
          {t('users.title')}
        </Typography>
        {isSuperAdmin && (
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => handleOpen()}
            sx={{
              background: 'linear-gradient(135deg, #1b5a5c 0%, #0a2829 100%)',
              color: '#ffffff',
              '&:hover': {
                background: 'linear-gradient(135deg, #328284 0%, #0F3D3E 100%)',
              },
              width: { xs: '100%', sm: 'auto' },
            }}
          >
            {t('users.add')}
          </Button>
        )}
      </Box>
      <Box
        sx={{
          mb: 3,
          p: 2,
          bgcolor: '#fff',
          borderRadius: 1,
          boxShadow: '0 2px 4px rgba(124, 58, 237, 0.05)',
        }}
      >
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              size="small"
              fullWidth
              placeholder={t('users.searchPh')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '&:hover fieldset': {
                    borderColor: '#0F3D3E',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#0F3D3E',
                  },
                },
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel id="users-role-filter-label">{t('users.filterRole')}</InputLabel>
              <Select
                labelId="users-role-filter-label"
                value={roleFilter}
                label={t('users.filterRole')}
                onChange={(e) => {
                  setRoleFilter(e.target.value);
                  setPage(0);
                }}
                sx={{
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(124, 58, 237, 0.35)',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#0F3D3E',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#0F3D3E',
                  },
                }}
              >
                <MenuItem value="">{t('users.filterRoleAll')}</MenuItem>
                <MenuItem value="Admin">{t('users.roleAdmin')}</MenuItem>
                <MenuItem value="Admin User">Admin User</MenuItem>
                <MenuItem value="Admin Event">Admin Event</MenuItem>
                <MenuItem value="Admin Accommodation">Admin Accommodation</MenuItem>
                <MenuItem value="Admin Contact">Admin Contact</MenuItem>
                <MenuItem value="Admin Promotion">Admin Promotion</MenuItem>
                <MenuItem value="User">{t('users.roleUser')}</MenuItem>
                <MenuItem value="Customer">{t('users.roleCustomer')}</MenuItem>
                <MenuItem value="Super Admin">{t('users.roleSuperAdmin')}</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid
            item
            xs={12}
            sm={6}
            md="auto"
            sx={{ display: 'flex', alignItems: 'center', ml: { md: 'auto' } }}
          >
            <Button
              variant="outlined"
              size="small"
              startIcon={<FilterAltOff />}
              onClick={() => {
                setSearchQuery('');
                setDebouncedNameSearch('');
                setRoleFilter('');
                setPage(0);
              }}
              sx={clearFilterButtonSx}
            >
              {t('common.clear')}
            </Button>
          </Grid>
        </Grid>
      </Box>

      {isLoading ? (
        <Box display="flex" justifyContent="center" my={4}>
          <CircularProgress sx={{ color: '#0F3D3E' }} />
        </Box>
      ) : (
        <TableContainer
          component={Paper}
          sx={{
            boxShadow: '0 2px 4px rgba(124, 58, 237, 0.05)',
            borderRadius: 2,
            '& .MuiTableCell-head': {
              backgroundColor: '#ebf4f4',
              color: '#0F3D3E',
              fontWeight: 600,
              padding: '16px'
            },
            '& .MuiTableCell-body': {
              padding: '16px'
            }
          }}
        >
          <Table sx={{ minWidth: 800 }}>
            <TableHead>
              <TableRow>

                <TableCell>{t('users.colName')}</TableCell>
                <TableCell>{t('common.email')}</TableCell>
                <TableCell>{t('users.colRole')}</TableCell>
                <TableCell>{t('common.status')}</TableCell>
                <TableCell>{t('common.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleUsers.map((user) => (
                <TableRow
                  key={user.userId}
                  sx={{
                    '&:hover': {
                      backgroundColor: '#ebf4f4',
                    }
                  }}
                >

                  <TableCell>
                    {`${user.firstName ?? user.FirstName ?? ''} ${user.lastName ?? user.LastName ?? ''}`.trim() ||
                      t('common.unknown')}
                  </TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>{normalizeRoleForDisplay(user.role)}</TableCell>
                  <TableCell>
                    <Box display="flex" gap={1}>
                      <Chip
                        size="small"
                        label={user.isEmailVerified ? t('users.verified') : t('users.unverified')}
                        color={user.isEmailVerified ? 'success' : 'warning'}
                      />
                      <Chip
                        size="small"
                        label={user.isBlocked ? t('users.blocked') : t('common.active')}
                        color={user.isBlocked ? 'error' : 'success'}
                      />
                    </Box>
                  </TableCell>
                  <TableCell>
                    <IconButton
                      onClick={() => handleOpen(user)}
                      sx={{ color: '#0F3D3E' }}
                    >
                      <Edit />
                    </IconButton>
                    {isSuperAdmin && (
                      <IconButton
                        onClick={() => handleDelete(user)}
                        disabled={deletingUserId === (user.userId ?? user.id)}
                        sx={{ color: '#ef4444' }}
                        title={t('users.deleteTitle')}
                      >
                        {deletingUserId === (user.userId ?? user.id) ? (
                          <CircularProgress size={20} sx={{ color: '#ef4444' }} />
                        ) : (
                          <Delete />
                        )}
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={totalCount}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(Number.parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[5, 10, 25, 50]}
            labelRowsPerPage={t('common.rowsPerPage')}
            sx={{
              '.MuiTablePagination-select': {
                color: '#0F3D3E',
              },
              '.MuiTablePagination-selectIcon': {
                color: '#0F3D3E',
              },
              '.MuiTablePagination-displayedRows': {
                color: '#0F3D3E',
              },
              '.MuiTablePagination-actions': {
                '& .MuiIconButton-root': {
                  color: '#0F3D3E',
                },
                '& .Mui-disabled': {
                  color: '#e9d5ff',
                }
              }
            }}
          />
        </TableContainer>
      )}

      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth fullScreen={isMobileView}>
        <DialogTitle>{selectedUser ? t('users.dialogEdit') : t('users.dialogAdd')}</DialogTitle>
        <DialogContent sx={{ position: 'relative' }}>
          {customerPreviewLoading && (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                zIndex: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'rgba(255,255,255,0.78)',
                borderRadius: 1,
              }}
            >
              <CircularProgress sx={{ color: '#0F3D3E' }} />
            </Box>
          )}
          <TextField
            margin="dense"
            name="email"
            label={t('common.email')}
            type="email"
            fullWidth
            required
            value={formData.email}
            onChange={handleChange}
          />
          <FormControl fullWidth margin="dense" required>
            <InputLabel>{t('users.colRole')}</InputLabel>
            <Select
              name="role"
              value={formData.role}
              label={t('users.colRole')}
              onChange={handleChange}
            >
              {selectedUser && formData.role === 'User' && (
                <MenuItem value="User">{t('users.roleUser')}</MenuItem>
              )}
              <MenuItem value="Admin">{t('users.roleAdmin')}</MenuItem>
              <MenuItem value="Admin User">Admin User</MenuItem>
              <MenuItem value="Admin Event">Admin Event</MenuItem>
              <MenuItem value="Admin Accommodation">Admin Accommodation</MenuItem>
              <MenuItem value="Admin Contact">Admin Contact</MenuItem>
              <MenuItem value="Admin Promotion">Admin Promotion</MenuItem>
              <MenuItem value="Customer">Customer</MenuItem>
              <MenuItem value="Super Admin">Super Admin</MenuItem>
            </Select>
          </FormControl>

          {isCustomerRole && (
            <FormControl fullWidth margin="dense" required>
              <InputLabel>{t('users.accountType')}</InputLabel>
              <Select
                name="accountType"
                value={formData.accountType}
                label={t('users.accountType')}
                onChange={handleChange}
              >
                <MenuItem value="Company">{t('users.accountCompany')}</MenuItem>
                <MenuItem value="Freelancer">{t('users.accountFreelancer')}</MenuItem>
              </Select>
            </FormControl>
          )}

          {!isCustomerRole && (
            <>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    margin="dense"
                    name="firstName"
                    label={t('users.fieldFirstName')}
                    fullWidth
                    required
                    value={formData.firstName}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    margin="dense"
                    name="lastName"
                    label={t('users.fieldLastName')}
                    fullWidth
                    required
                    value={formData.lastName}
                    onChange={handleChange}
                  />
                </Grid>
              </Grid>
            </>
          )}

          {isCustomerRole && !isFreelancerAccount && (
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  margin="dense"
                  label={t('users.fullName')}
                  fullWidth
                  required
                  value={businessName}
                  onChange={(e) => setBusinessName(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  margin="dense"
                  name="phoneNumber"
                  label={t('users.phoneNumber')}
                  fullWidth
                  required
                  value={formData.phoneNumber}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth margin="dense" required>
                  <InputLabel>{t('users.serviceType')}</InputLabel>
                  <Select
                    value={companyServiceType}
                    label={t('users.serviceType')}
                    onChange={(e) => setCompanyServiceType(e.target.value)}
                    disabled={serviceTypesLoading}
                  >
                    {serviceTypes.map((service) => {
                      const sid = getCategoryId(service);
                      return (
                        <MenuItem key={String(sid)} value={String(sid)}>
                          {getCategoryTitleEn(service)}
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  margin="dense"
                  name="country"
                  label={t('users.country')}
                  fullWidth
                  required
                  value={formData.country}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth margin="dense" required>
                  <InputLabel>{t('common.city')}</InputLabel>
                  <Select
                    value={city}
                    label={t('common.city')}
                    onChange={async (e) => {
                      const val = e.target.value;
                      setCity(val);
                      setDistrict('');
                      await fetchDistrictOptions(val, 'company');
                    }}
                    disabled={citiesLoading}
                  >
                    {cities.map((c) => (
                      <MenuItem key={c.id || c.cityId} value={c.id || c.cityId}>
                        {c.name || c.cityName}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth margin="dense" required>
                  <InputLabel>{t('common.district')}</InputLabel>
                  <Select
                    value={district}
                    label={t('common.district')}
                    onChange={(e) => setDistrict(e.target.value)}
                    disabled={companyDistrictsLoading}
                  >
                    {companyDistricts.map((d) => (
                      <MenuItem key={d.id || d.districtId} value={d.id || d.districtId}>
                        {d.name || d.districtName}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField margin="dense" label={t('users.zipcode')} fullWidth required value={zipcode} onChange={(e) => setZipcode(e.target.value)} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField margin="dense" label={t('users.name')} fullWidth required value={ownerName} onChange={(e) => setOwnerName(e.target.value)} />
              </Grid>
              {renderDocumentsGrid2x3(t('users.companyDocs'), [
                {
                  key: 'companyRegDoc',
                  render: () =>
                    renderDocumentCategorySlot(
                      t('users.docProofBusinessReg'),
                      companyRegDoc,
                      setCompanyRegDoc,
                      true
                    ),
                },
                {
                  key: 'nationalIdDoc',
                  render: () =>
                    renderDocumentCategorySlot(
                      t('users.docPassportPermit'),
                      nationalIdDoc,
                      setNationalIdDoc,
                      true
                    ),
                },
              ])}
            </Grid>
          )}

          {isCustomerRole && isFreelancerAccount && (
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  margin="dense"
                  name="firstName"
                  label={t('users.fieldFirstName')}
                  fullWidth
                  required
                  value={formData.firstName}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  margin="dense"
                  name="lastName"
                  label={t('users.fieldLastName')}
                  fullWidth
                  required
                  value={formData.lastName}
                  onChange={handleChange}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  margin="dense"
                  name="phoneNumber"
                  label={t('users.phoneNumber')}
                  fullWidth
                  required
                  value={formData.phoneNumber}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  margin="dense"
                  name="country"
                  label={t('users.country')}
                  fullWidth
                  required
                  value={formData.country}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth margin="dense" required>
                  <InputLabel>{t('users.serviceType')}</InputLabel>
                  <Select
                    value={freelancerServiceType}
                    label={t('users.serviceType')}
                    onChange={(e) => setFreelancerServiceType(e.target.value)}
                    disabled={serviceTypesLoading}
                  >
                    {serviceTypes.map((service) => {
                      const sid = getCategoryId(service);
                      return (
                        <MenuItem key={String(sid)} value={String(sid)}>
                          {getCategoryTitleEn(service)}
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth margin="dense" required>
                  <InputLabel>{t('common.city')}</InputLabel>
                  <Select
                    value={freelancerCity}
                    label={t('common.city')}
                    onChange={async (e) => {
                      const val = e.target.value;
                      setFreelancerCity(val);
                      setFreelancerDistrict('');
                      await fetchDistrictOptions(val, 'freelancer');
                    }}
                    disabled={citiesLoading}
                  >
                    {cities.map((c) => (
                      <MenuItem key={c.id || c.cityId} value={c.id || c.cityId}>
                        {c.name || c.cityName}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth margin="dense" required>
                  <InputLabel>{t('common.district')}</InputLabel>
                  <Select
                    value={freelancerDistrict}
                    label={t('common.district')}
                    onChange={(e) => setFreelancerDistrict(e.target.value)}
                    disabled={freelancerDistrictsLoading}
                  >
                    {freelancerDistricts.map((d) => (
                      <MenuItem key={d.id || d.districtId} value={d.id || d.districtId}>
                        {d.name || d.districtName}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField margin="dense" label={t('users.zipcode')} fullWidth required value={freelancerZipcode} onChange={(e) => setFreelancerZipcode(e.target.value)} />
              </Grid>
              {renderDocumentsGrid2x3(t('users.freelancerDocs'), [
                {
                  key: 'freelancerNationalIdDoc',
                  render: () =>
                    renderDocumentCategorySlot(
                      t('users.docPassportPermit'),
                      freelancerNationalIdDoc,
                      setFreelancerNationalIdDoc,
                      true
                    ),
                },
              ])}
            </Grid>
          )}

          <Box mt={2} display="flex" gap={2}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.isEmailVerified}
                  onChange={(e) => setFormData(prev => ({ ...prev, isEmailVerified: e.target.checked }))}
                  color="success"
                />
              }
              label={t('users.switchEmailVerified')}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={formData.isBlocked}
                  onChange={(e) => setFormData(prev => ({ ...prev, isBlocked: e.target.checked }))}
                  color="error"
                />
              }
              label={t('users.switchBlocked')}
            />
          </Box>

        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={isSubmitting}>{t('common.cancel')}</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            color="primary"
            disabled={isSubmitting}
            startIcon={isSubmitting ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : null}
          >
            {selectedUser ? t('common.update') : t('common.create')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={toastOpen}
        autoHideDuration={6000}
        onClose={() => setToastOpen(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setToastOpen(false)} severity={toast.type} sx={{ width: '100%' }}>
          {toast.text}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Users;
